import {
  CHANNEL_NAMES,
  FINANCE_KIND_LABELS,
  MARKETPLACE_CHANNELS,
  PROFIT_STATE_LABELS,
  type CargoSource,
  type FinanceKind,
  type ProductCostView,
  type ProfitAccountRow,
  type ProfitEstimated,
  type ProfitFigures,
  type ProfitOrderRow,
  type ProfitProductRow,
  type ProfitReport,
  type ProfitState,
  type ProfitSyncState,
} from '@wms/domain';
import { csvDecimal } from './csv';
import { decimalInput } from './format';

// Kârlılık: how its figures read. Amounts come as decimal strings ("-12.50"), deductions and costs below zero.

/** Kesin green, Kısmi blue, Tahmini amber */
export const STATE_TONE: Readonly<Record<ProfitState, 'ok' | 'info' | 'warn'>> = { final: 'ok', partial: 'info', estimate: 'warn' };

export const stateLabel = (state: ProfitState) => PROFIT_STATE_LABELS[state];

/** An amount below zero (a loss, a deduction); "-0.00" is none. */
export const below = (amount: string) => Number(amount) < 0;

/** "%18,5", "−%10"; "—" without a ciro */
export function marginText(margin: number | null): string {
  if (margin === null) return '—';
  const pct = Math.abs(margin * 100).toLocaleString('tr-TR', { maximumFractionDigits: 1 });
  return `${margin < 0 && pct !== '0' ? '−' : ''}%${pct}`;
}

/** What the marketplace's figures of an order were worked out from, when its records were not in. */
const ESTIMATED_TEXT: Readonly<Record<ProfitEstimated, string>> = { ciro: 'ciro', commission: 'komisyon', cargo: 'kargo', fees: 'hizmet bedeli' };

/** "Tahmini: komisyon, kargo"; null when every figure is the marketplace's own */
export function estimatedText(parts: readonly ProfitEstimated[]): string | null {
  return parts.length === 0 ? null : `Tahmini: ${parts.map((p) => ESTIMATED_TEXT[p]).join(', ')}`;
}

export const CARGO_SOURCE_TEXT: Readonly<Record<CargoSource, string>> = {
  rows: 'Pazaryerinin kargo faturasından',
  warehouse: 'Pronto Depo’nun kargo anlaşmasından (depo ücreti)',
  estimate: 'Tahmini: son 90 günün benzer gönderilerinden',
  unknown: 'Bilinmiyor: kendi kargo anlaşmanız ya da karşılaştırılacak gönderi yok',
  none: 'Kargoya verilmedi',
};

/**
 * The Kesintiler tab's chip of an account's records: taken off the profit (a fee, an ad), added to it (a
 * compensation the marketplace paid), or left out of it (payouts, documents, stopaj).
 */
export function accountChip(a: Pick<ProfitAccountRow, 'counted' | 'net'>): { readonly text: string; readonly tone: 'bad' | 'ok' | '' } {
  if (!a.counted) return { text: 'Katılmadı', tone: '' };
  return Number(a.net) > 0 ? { text: 'Kâra eklendi', tone: 'ok' } : { text: 'Düşüldü', tone: 'bad' };
}

/**
 * What an order's line says of the marketplace's records it does not have: a marketplace whose records are read
 * sends them after delivery; one whose records are not read never does (its deductions are unknown); a manual
 * order has none to wait for.
 */
export function noRecordsText(channel: string, supported: boolean): string {
  if (channel === 'manual') return 'Pazaryerinin bu siparişe ait kaydı yok.';
  return supported ? 'Pazaryerinin bu siparişe ait kaydı yok (kayıtlar teslimattan sonra gelir).' : 'Bu pazaryerinin finans kayıtları okunmuyor; kesintileri bilinmiyor.';
}

/** "Komisyon" or "Kargo · Gönderi Kargo Bedeli": our name of the kind, the marketplace's own when it says more */
export function kindText(kind: FinanceKind, label: string): string {
  const ours = FINANCE_KIND_LABELS[kind];
  return label && label.toLocaleLowerCase('tr') !== ours.toLocaleLowerCase('tr') ? `${ours} · ${label}` : ours;
}

/**
 * The ciro the order and product tables show: KDV hariç as every other column of theirs, so a row adds up (ciro +
 * kesintiler + Pronto Depo + maliyet = net kâr) and its margin is the profit over it. The KDV-dahil ciro is the
 * tile's and the CSV's.
 */
export const TABLE_CIRO = { label: 'Ciro (KDV hariç)', of: (f: Pick<ProfitFigures, 'ciroNet'>): string => f.ciroNet } as const;

/** One bar of the waterfall from the ciro to the net profit, its ends in lira. */
export interface WaterfallStep {
  readonly key: string;
  readonly label: string;
  readonly amount: number;
  readonly from: number;
  readonly to: number;
  /** a total drawn from zero (the ciro, the profit), else a step from the running total */
  readonly total: boolean;
}

const STEPS: readonly (readonly [keyof ProfitFigures, string])[] = [
  ['commission', 'Komisyon'],
  ['cargo', 'Kargo'],
  ['fees', 'Platform ve hizmet bedelleri'],
  ['returns', 'İadeler ve indirimler'],
  ['other', 'Ceza, reklam ve diğer'],
  ['pronto', 'Pronto Depo hizmetleri'],
  ['productCost', 'Ürün maliyeti'],
];

/**
 * From the ciro (KDV hariç) to the net profit, step by step; a step of nothing is left out. `min` and `max`
 * bound every bar and zero, so the bars can be placed on one scale.
 */
export function waterfall(f: ProfitFigures): { readonly steps: readonly WaterfallStep[]; readonly min: number; readonly max: number } {
  const ciro = Number(f.ciroNet);
  const steps: WaterfallStep[] = [{ key: 'ciro', label: 'Ciro (KDV hariç)', amount: ciro, from: 0, to: ciro, total: true }];
  let running = ciro;
  for (const [key, label] of STEPS) {
    const amount = Number(f[key]);
    if (amount === 0) continue;
    steps.push({ key, label, amount, from: running, to: running + amount, total: false });
    running += amount;
  }
  const profit = Number(f.profit);
  steps.push({ key: 'profit', label: 'Net kâr (KDV hariç)', amount: profit, from: 0, to: profit, total: true });
  const ends = steps.flatMap((s) => [s.from, s.to]);
  return { steps, min: Math.min(0, ...ends), max: Math.max(0, ...ends) };
}

/** Where a bar sits on the scale, in percent of its width: { left, width }; a step too small to see is a sliver. */
export function barBox(step: Pick<WaterfallStep, 'from' | 'to'>, min: number, max: number): { readonly left: number; readonly width: number } {
  const span = max - min || 1;
  const lo = Math.min(step.from, step.to);
  const hi = Math.max(step.from, step.to);
  const round = (n: number) => Math.round(n * 100) / 100;
  return { left: round(((lo - min) / span) * 100), width: round(Math.max(0.4, ((hi - lo) / span) * 100)) };
}

/** The orders' table as the CSV export writes it; `day` writes an order's date. */
export function ordersCsv(orders: readonly ProfitOrderRow[], day: (iso: string) => string): (string | number)[][] {
  return [
    [
      'Sipariş no',
      'Sipariş tarihi',
      'Pazaryeri',
      'Hesap',
      'Durum',
      'Ciro (KDV dahil)',
      'Ciro (KDV hariç)',
      'Komisyon',
      'Kargo',
      'Platform ve hizmet bedelleri',
      'İadeler ve indirimler',
      'Ceza, reklam ve diğer',
      'Pazaryeri kesintileri',
      'Pronto Depo hizmetleri',
      'Ürün maliyeti',
      'Net kâr (KDV hariç)',
      'Kâr marjı',
      'Pazaryerinden hak ediş (KDV dahil)',
      'Stopaj',
      'Tahmini olanlar',
    ],
    ...orders.map((o) => [
      o.orderNumber,
      day(o.orderedAt),
      CHANNEL_NAMES[o.channel] ?? o.channel,
      o.account ?? '',
      stateLabel(o.state),
      csvDecimal(o.ciro),
      csvDecimal(o.ciroNet),
      csvDecimal(o.commission),
      csvDecimal(o.cargo),
      csvDecimal(o.fees),
      csvDecimal(o.returns),
      csvDecimal(o.other),
      csvDecimal(o.marketplace),
      csvDecimal(o.pronto),
      csvDecimal(o.productCost),
      csvDecimal(o.profit),
      o.margin === null ? '' : csvDecimal((Math.round(o.margin * 1000) / 10).toFixed(1)),
      csvDecimal(o.cash),
      csvDecimal(o.withholding),
      o.estimated.map((p) => ESTIMATED_TEXT[p]).join(', '),
    ]),
  ];
}

/** A stored unit cost as its field shows it: "12.5000" → "12,50", "1.2345" → "1,2345"; "" for none */
export function costFieldText(unitCost: string | null): string {
  if (unitCost === null) return '';
  const [whole = '0', fraction = ''] = unitCost.split('.');
  const kept = fraction.replace(/0+$/, '');
  return `${whole},${kept.length < 2 ? kept.padEnd(2, '0') : kept}`;
}

/** "1.250", "12.500", "1.000.000": a thousands dot to a Turkish eye (1250), a decimal point to the field (1,25) */
const THOUSANDS_DOTTED = /^[1-9]\d{0,2}(\.\d{3})+$/;
/** "1.250,00": thousands dotted and a decimal comma, as Turkish writes 1250 */
const DOTTED_WITH_COMMA = /^\d{1,3}(\.\d{3})+,\d+$/;

/** Said when a cost was typed with a thousands dot: whether "1.250" is 1250 or 1,25 is not guessed. */
export const COST_THOUSANDS_TEXT = 'Binlik ayırıcı kullanmayın: 1250 ya da 1250,00 yazın (kuruş virgülle: 12,50)';

/**
 * What a typed cost means: a cost ("12.50"), cleared (an empty field), or not a cost. A dotted thousands group
 * alone ("1.250") is refused (`thousands`): saved as 1,25 it would price every past order a thousandth of it,
 * for good (the first cost applies to the orders before it); with a decimal comma ("1.250,00") it is plain.
 */
export function costInput(text: string): { readonly ok: true; readonly value: string | null } | { readonly ok: false; readonly thousands?: true } {
  const typed = text.trim().replace(/\s/g, '');
  if (typed === '') return { ok: true, value: null };
  if (THOUSANDS_DOTTED.test(typed)) return { ok: false, thousands: true };
  const value = decimalInput(DOTTED_WITH_COMMA.test(typed) ? typed.replace(/\./g, '') : typed, 4);
  return value === null || !/^\d{1,10}(\.\d{1,4})?$/.test(value) ? { ok: false } : { ok: true, value };
}

/** The same cost, however it is written ("12.5" and "12.5000"; none and none). */
export const sameCost = (a: string | null, b: string | null) => (a === null || b === null ? a === b : Number(a) === Number(b));

/** How long a "Şimdi güncelle" counts as under way: the read takes minutes, a worker that never took it is not waited for. */
const ASKED_MS = 15 * 60_000;

/**
 * A read of the account is under way: the server says so (a long first read, the night's), or "Şimdi güncelle"
 * was asked for and the read has not ended since (in the last 15 minutes: a worker that never took it is not
 * waited for).
 */
export function syncAsked(s: Pick<ProfitSyncState, 'supported' | 'requestedAt' | 'checkedAt' | 'running'>, now = Date.now()): boolean {
  if (!s.supported) return false;
  if (s.running) return true;
  if (s.requestedAt === null || Date.parse(s.requestedAt) < now - ASKED_MS) return false;
  return s.checkedAt === null || Date.parse(s.checkedAt) < Date.parse(s.requestedAt);
}

/** An account's finance read as the banner says it; `stamp` writes a moment. */
export function syncText(s: ProfitSyncState, stamp: (iso: string) => string, now = Date.now()): { readonly tone: 'ok' | 'info' | 'bad'; readonly text: string } {
  const name = `${s.label} (${CHANNEL_NAMES[s.channel] ?? s.channel})`;
  if (!s.active) return { tone: 'info', text: `${name}: hesap kapalı ya da anahtarı geçersiz; finans kayıtları okunmuyor` };
  if (!s.supported) return { tone: 'info', text: `${name}: bu pazaryerinin finans kayıtları okunamıyor; pazaryeri kesintileri bilinmiyor` };
  // asked for again (a role just given): the read under way says more than the failure before it
  if (syncAsked(s, now)) {
    // asked for and not read since: when (the night's read was asked for by nobody)
    const asked = s.requestedAt !== null && (s.checkedAt === null || Date.parse(s.checkedAt) < Date.parse(s.requestedAt));
    return { tone: 'info', text: `${name}: finans kayıtları okunuyor${asked ? ` (${stamp(s.requestedAt!)} istendi)` : ''}; birkaç dakika sürebilir` };
  }
  if (s.error) return { tone: 'bad', text: `${name}: ${s.error}` };
  if (s.syncedUntil) return { tone: 'ok', text: `${name}: finans kayıtları ${stamp(s.syncedUntil)} itibarıyla okundu` };
  return { tone: 'info', text: `${name}: finans kayıtları henüz okunmadı; her gece 04:15’te okunur` };
}

/** The line about orders whose marketplace records have not all come; null when none waits. */
export function pendingText(counts: ProfitReport['counts']): string | null {
  if (counts.pending === 0) return null;
  return `${counts.pending.toLocaleString('tr-TR')} siparişin pazaryeri kesintileri teslimattan sonra kesinleşir; şimdilik tahmini (Tahmini / Kısmi).`;
}

/** The line about orders whose cargo is not known here (the seller's own contract, or no parcel to go by); null for none. */
export function cargoUnknownText(counts: ProfitReport['counts']): string | null {
  if (counts.cargoUnknown === 0) return null;
  return `${counts.cargoUnknown.toLocaleString('tr-TR')} siparişin kargo ücreti bilinmiyor (kendi kargo anlaşmanızla gönderildi ya da karşılaştırılacak kargo faturası henüz yok): net kâra katılmadı.`;
}

/**
 * The cost table: the products sold in the period first (most units first), then the rest by SKU; `query`
 * keeps those whose SKU or name holds it.
 */
export function costRows(costs: readonly ProductCostView[], sold: readonly ProfitProductRow[], query = ''): (ProductCostView & { readonly sold: number })[] {
  const units = new Map(sold.flatMap((p) => (p.productId ? [[p.productId, p.units] as const] : [])));
  const q = query.trim().toLocaleLowerCase('tr');
  return costs
    .filter((c) => q === '' || c.sku.toLocaleLowerCase('tr').includes(q) || c.name.toLocaleLowerCase('tr').includes(q))
    .map((c) => ({ ...c, sold: units.get(c.productId) ?? 0 }))
    .sort((a, b) => b.sold - a.sold || a.sku.localeCompare(b.sku, 'tr'));
}

/** The channel chips: every marketplace the seller has an account on or an order from, in the usual order. */
export function channelChoices(report: Pick<ProfitReport, 'channels' | 'sync'>, seen: readonly string[] = []): string[] {
  const all = new Set<string>([...seen, ...report.channels.map((c) => c.channel), ...report.sync.map((s) => s.channel)]);
  return MARKETPLACE_CHANNELS.filter((c) => all.has(c));
}
